import { useState } from "react";
import { X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import LoginForm from "@/components/auth/LoginForm";
import Login2FAForm from "@/components/auth/Login2FAForm";
import RegisterForm from "@/components/auth/RegisterForm";
import VerifyForm from "@/components/auth/VerifyForm";
import ForgotPasswordForm from "@/components/auth/ForgotPasswordForm";
import ResetPasswordForm from "@/components/auth/ResetPasswordForm";

const AuthModal = ({ isOpen, onClose }) => {
  const { login, verifyLogin } = useAuth();
  const [mode, setMode] = useState("login");

  // Login 2FA state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginEmailSent, setLoginEmailSent] = useState(false);
  const [loginDisplayCode, setLoginDisplayCode] = useState("");

  // Registration verification state
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyCode, setVerifyCode] = useState("");

  // Password reset state
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetDisplayCode, setResetDisplayCode] = useState("");

  if (!isOpen) return null;

  const resetAndClose = () => {
    setMode("login");
    setLoginEmail("");
    setLoginEmailSent(false);
    setLoginDisplayCode("");
    setVerifyEmail("");
    setVerifyCode("");
    setResetEmail("");
    setResetEmailSent(false);
    setResetDisplayCode("");
    onClose();
  };

  const handleLogin = async (email, password) => {
    const data = await login(email, password);
    if (data.requires_2fa) {
      setLoginEmail(data.email);
      setLoginEmailSent(data.email_sent);
      setLoginDisplayCode(data.code || "");
      setMode("login-2fa");
    } else {
      resetAndClose();
    }
  };

  const handleLogin2FAVerified = () => {
    resetAndClose();
  };

  const handleCodeSent = ({ code, email }) => {
    setVerifyCode(code);
    setVerifyEmail(email);
    setMode("verify");
  };

  const handleResetCodeSent = ({ email, emailSent, displayCode }) => {
    setResetEmail(email);
    setResetEmailSent(emailSent);
    setResetDisplayCode(displayCode || "");
    setMode("reset-code");
  };

  const handleResetSuccess = () => {
    setMode("login");
    setResetEmail("");
    setResetEmailSent(false);
    setResetDisplayCode("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}>
        <button onClick={resetAndClose} className="absolute top-4 right-4 transition-colors z-10"
          style={{ color: "var(--color-text-dim)" }} data-testid="close-auth-modal">
          <X className="w-5 h-5" />
        </button>

        <div className="p-8">
          {mode === "login" && (
            <LoginForm
              onLogin={handleLogin}
              onSwitchToRegister={() => setMode("register")}
              onSwitchToForgot={() => setMode("forgot")}
            />
          )}
          {mode === "login-2fa" && (
            <Login2FAForm
              email={loginEmail}
              emailSent={loginEmailSent}
              displayCode={loginDisplayCode}
              onVerified={handleLogin2FAVerified}
              onBack={() => setMode("login")}
            />
          )}
          {mode === "register" && (
            <RegisterForm
              onCodeSent={handleCodeSent}
              onSwitchToLogin={() => setMode("login")}
            />
          )}
          {mode === "verify" && (
            <VerifyForm
              email={verifyEmail}
              displayCode={verifyCode}
              onBack={() => setMode("register")}
            />
          )}
          {mode === "forgot" && (
            <ForgotPasswordForm
              onCodeSent={handleResetCodeSent}
              onBack={() => setMode("login")}
            />
          )}
          {mode === "reset-code" && (
            <ResetPasswordForm
              email={resetEmail}
              emailSent={resetEmailSent}
              displayCode={resetDisplayCode}
              onSuccess={handleResetSuccess}
              onBack={() => setMode("forgot")}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
